/**
 * Resolución de precio por tramo (SUP-ORD-008).
 * Misma regla que supply_management.price_tiers.pick_tier_for_quantity:
 * intervalos [quantity_from, quantity_to] con QTA_LIMITE Gamma como tope inclusive.
 */

export interface SupplyPriceTier {
  quantity_from: number;
  quantity_to: number | null;
  final_price: number;
}

export interface SupplierPricingInput {
  last_purchase_price: number;
  base_unit_price?: number;
  price_tiers?: SupplyPriceTier[];
  price_from_tier?: boolean;
}

export type TierPriceChangeKind = 'same' | 'discount' | 'increase';

export interface SupplyPriceTierWithDelta extends SupplyPriceTier {
  /** Diferencia de precio unitario vs tramo anterior (null en el primero). */
  deltaFromPrevious: number | null;
  changeKind: TierPriceChangeKind;
}

export function sortTiersAscending(tiers: SupplyPriceTier[]): SupplyPriceTier[] {
  return [...tiers].sort((a, b) => a.quantity_from - b.quantity_from);
}

/** Tramos ordenados con delta vs tramo anterior (descuento / aumento). */
export function tiersWithPriceDeltas(tiers: SupplyPriceTier[]): SupplyPriceTierWithDelta[] {
  const sorted = sortTiersAscending(tiers);
  return sorted.map((tier, index) => {
    if (index === 0) {
      return { ...tier, deltaFromPrevious: null, changeKind: 'same' as const };
    }
    const prev = sorted[index - 1];
    const delta = Number(tier.final_price) - Number(prev.final_price);
    let changeKind: TierPriceChangeKind = 'same';
    if (delta < -1e-9) changeKind = 'discount';
    else if (delta > 1e-9) changeKind = 'increase';
    return { ...tier, deltaFromPrevious: delta, changeKind };
  });
}

export function pickTierForQuantity(
  tiers: SupplyPriceTier[],
  quantity: number,
): SupplyPriceTier | null {
  if (!tiers.length || quantity <= 0) {
    return null;
  }
  const sorted = [...tiers].sort((a, b) => b.quantity_from - a.quantity_from);
  for (const tier of sorted) {
    if (tier.quantity_from > quantity) {
      continue;
    }
    if (tier.quantity_to != null && tier.quantity_to < quantity) {
      continue;
    }
    return tier;
  }
  return null;
}

export function resolveUnitPriceForSupplier(
  supplier: SupplierPricingInput,
  finalQuantity: number,
): { unitPrice: number; priceFromTier: boolean } {
  const tiers = supplier.price_tiers ?? [];
  const fallback =
    Number(supplier.base_unit_price ?? supplier.last_purchase_price) || 0;
  if (finalQuantity > 0 && tiers.length > 0) {
    const picked = pickTierForQuantity(tiers, finalQuantity);
    if (picked) {
      return { unitPrice: picked.final_price, priceFromTier: true };
    }
  }
  return { unitPrice: fallback, priceFromTier: false };
}

export function resolveLinePricing(
  supplier: SupplierPricingInput,
  finalQuantity: number,
): {
  last_purchase_price: number;
  suggested_line_total: number;
  price_from_tier: boolean;
} {
  const { unitPrice, priceFromTier } = resolveUnitPriceForSupplier(
    supplier,
    finalQuantity,
  );
  return {
    last_purchase_price: unitPrice,
    suggested_line_total: finalQuantity * unitPrice,
    price_from_tier: priceFromTier,
  };
}
